import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery, useMutation } from 'react-query'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Check,
  Zap,
  Crown,
  Building2,
  Loader2,
  CreditCard,
  AlertCircle,
  ArrowRight,
  Lock
} from 'lucide-react'
import { apiService } from '../../services/api'
import { toast } from 'react-hot-toast'
import { useBranding } from '../../context/BrandingContext'

const PLAN_ICONS = {
  basic:      Zap,
  pro:        Crown,
  enterprise: Building2,
}
const STANDARD_ORDER = ['basic', 'pro', 'enterprise']
const getPlanIcon = (planId) => PLAN_ICONS[planId] || Zap

// Plan accent colors via inline styles
const PLAN_COLORS = {
  basic:      { color: '#3b82f6', bg: 'rgba(59,130,246,0.1)',  border: 'rgba(59,130,246,0.2)' },
  pro:        { color: '#6c63ff', bg: 'rgba(108,99,255,0.1)',  border: 'rgba(108,99,255,0.3)' },
  enterprise: { color: '#8b5cf6', bg: 'rgba(139,92,246,0.1)', border: 'rgba(139,92,246,0.2)' },
}
const getPlanColor = (planId) => PLAN_COLORS[planId] || PLAN_COLORS.pro

// ── Activating screen (post-Stripe redirect) ─────────────────────────────────
const ActivatingScreen = () => (
  <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-0)' }}>
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem', textAlign: 'center', maxWidth: '24rem', padding: '0 1.5rem' }}
    >
      <div style={{ width: '4rem', height: '4rem', borderRadius: '50%', background: 'var(--accent-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader2 style={{ width: '2rem', height: '2rem', color: 'var(--accent)', animation: 'spin 1s linear infinite' }} />
      </div>
      <div>
        <h2 className="font-bold" style={{ fontSize: '1.5rem', color: 'var(--text)', marginBottom: '0.5rem' }}>
          Activating your subscription
        </h2>
        <p className="text-muted">
          Payment received — setting up your account. This only takes a moment.
        </p>
      </div>
      <div className="flex items-center gap-2 text-faint" style={{ fontSize: '0.875rem' }}>
        <Lock style={{ width: '1rem', height: '1rem' }} />
        <span>Secured by Stripe</span>
      </div>
    </motion.div>
  </div>
)

// ── Plan card ─────────────────────────────────────────────────────────────────
const PlanCard = ({ planId, plan, isSelected, isHighlighted, onSelect }) => {
  const Icon = getPlanIcon(planId)
  const colors = getPlanColor(planId)

  const featureList = [
    plan.features?.sites
      ? `${plan.features.sites === 'unlimited' ? 'Unlimited' : plan.features.sites} website${plan.features.sites === 1 ? '' : 's'}`
      : null,
    plan.features?.editsPerMonth
      ? `${plan.features.editsPerMonth === 'unlimited' ? 'Unlimited' : plan.features.editsPerMonth} edits/month`
      : null,
    plan.features?.storage || null,
    plan.features?.support || null,
    plan.features?.analytics ? 'Analytics' : null,
    plan.features?.backup ? 'Automated backups' : null,
  ].filter(Boolean)

  return (
    <motion.button
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.99 }}
      onClick={() => onSelect(planId)}
      style={{
        position: 'relative',
        width: '100%',
        textAlign: 'left',
        borderRadius: 'var(--radius-lg)',
        border: isSelected ? `2px solid var(--accent)` : `2px solid var(--border)`,
        padding: '1.5rem',
        background: 'var(--surface)',
        cursor: 'pointer',
        transition: 'all 0.2s',
        boxShadow: isSelected ? 'var(--shadow)' : 'none',
      }}
    >
      {isHighlighted && (
        <div style={{ position: 'absolute', top: '-0.75rem', left: '50%', transform: 'translateX(-50%)' }}>
          <span style={{ padding: '0.25rem 0.75rem', borderRadius: '9999px', fontSize: '0.75rem', fontWeight: 600, background: 'var(--accent)', color: 'white' }}>
            Most popular
          </span>
        </div>
      )}

      <div className="flex items-start justify-between" style={{ marginBottom: '1rem' }}>
        <div style={{ padding: '0.5rem', borderRadius: 'var(--radius)', background: colors.bg }}>
          <Icon style={{ width: '1.25rem', height: '1.25rem', color: colors.color }} />
        </div>
        {isSelected && (
          <div style={{ width: '1.25rem', height: '1.25rem', borderRadius: '50%', background: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Check style={{ width: '0.75rem', height: '0.75rem', color: 'white' }} />
          </div>
        )}
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <h3 className="font-bold" style={{ fontSize: '1.125rem', color: 'var(--text)' }}>
          {plan.name || planId.charAt(0).toUpperCase() + planId.slice(1)}
        </h3>
        <div className="flex items-baseline gap-1" style={{ marginTop: '0.25rem' }}>
          <span className="font-bold" style={{ fontSize: '1.875rem', color: 'var(--text)' }}>
            £{plan.price}
          </span>
          <span className="text-muted" style={{ fontSize: '0.875rem' }}>/month</span>
        </div>
      </div>

      <ul style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {featureList.map((feature, i) => (
          <li key={i} className="flex items-center gap-2 text-soft" style={{ fontSize: '0.875rem' }}>
            <Check style={{ width: '0.875rem', height: '0.875rem', color: 'var(--accent)', flexShrink: 0 }} />
            {feature}
          </li>
        ))}
      </ul>
    </motion.button>
  )
}

// ── Main gate ─────────────────────────────────────────────────────────────────
const SubscriptionGate = ({ children }) => {
  const { cmsName } = useBranding()
  const location = useLocation()
  const navigate = useNavigate()
  const [selectedPlan, setSelectedPlan] = useState(null)

  const isSuccessPage = location.pathname.endsWith('/billing/success')
  const sessionId = new URLSearchParams(location.search).get('session_id')
  const isPostPayment = isSuccessPage && !!sessionId

  const { data: profile, isLoading: profileLoading } = useQuery(
    'client-profile',
    () => apiService.get('/clients/profile').then(r => r.data),
    {
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      staleTime: 0,
      refetchInterval: isPostPayment ? 3000 : false,
    }
  )

  const { data: plansRaw } = useQuery(
    'billing-plans',
    () => apiService.get('/billing/plans').then(r => r.data),
    { enabled: !profileLoading }
  )

  const billingStatus = profile?.billing_status
  const assignedPlanPrice = plansRaw?.[profile?.subscription_plan]?.price
  const isFreePlan = assignedPlanPrice === 0
  const isActive = billingStatus === 'active' || billingStatus === 'trialing' || isFreePlan

  useEffect(() => {
    if (profile && !selectedPlan) {
      setSelectedPlan(profile.subscription_plan || 'pro')
    }
  }, [profile])

  const checkoutMutation = useMutation(
    (planId) => apiService.post('/billing/create-checkout-session', { planId }).then(r => r.data),
    {
      onSuccess: (data) => {
        if (data.checkoutUrl) {
          window.location.href = data.checkoutUrl
        } else {
          toast.error('No checkout URL returned. Please try again.')
        }
      },
      onError: () => toast.error('Failed to start checkout. Please try again.')
    }
  )

  useEffect(() => {
    if (isPostPayment && isActive) {
      navigate('/client', { replace: true })
    }
  }, [isActive, isPostPayment, navigate])

  if (profileLoading) return <>{children}</>
  if (isActive) return <>{children}</>
  if (isPostPayment) return <ActivatingScreen />

  const plans = plansRaw
    ? [
        ...STANDARD_ORDER.filter(id => plansRaw[id]).map(id => ({ id, ...plansRaw[id] })),
        ...Object.entries(plansRaw)
          .filter(([id, p]) => !STANDARD_ORDER.includes(id) && p.custom)
          .map(([id, p]) => ({ id, ...p }))
      ]
    : []

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 50, overflowY: 'auto' }}>
      {/* Blurred backdrop */}
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,14,18,0.8)', backdropFilter: 'blur(4px)' }} />

      <div style={{ position: 'relative', minHeight: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '3rem 1rem' }}>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          style={{ width: '100%', maxWidth: '56rem' }}
        >
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 0.75rem', borderRadius: '9999px', background: 'var(--accent-soft)', border: '1px solid var(--border)', color: 'var(--accent)', fontSize: '0.875rem', fontWeight: 500, marginBottom: '1rem' }}>
              <CreditCard style={{ width: '1rem', height: '1rem' }} />
              Subscription required
            </div>
            <h1 className="font-bold" style={{ fontSize: '1.875rem', color: 'white', marginBottom: '0.75rem' }}>
              Activate your {cmsName} subscription
            </h1>
            <p className="text-muted" style={{ maxWidth: '28rem', margin: '0 auto' }}>
              Choose a plan to unlock your dashboard and start managing your website.
            </p>
          </div>

          {/* Plan cards */}
          {plans.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>
              {plans.map(plan => (
                <PlanCard
                  key={plan.id}
                  planId={plan.id}
                  plan={plan}
                  isSelected={selectedPlan === plan.id}
                  isHighlighted={plan.id === profile?.subscription_plan}
                  onSelect={setSelectedPlan}
                />
              ))}
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '10rem', marginBottom: '2rem' }}>
              <Loader2 style={{ width: '1.5rem', height: '1.5rem', color: 'var(--text-faint)', animation: 'spin 1s linear infinite' }} />
            </div>
          )}

          {/* CTA */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
            <button
              className="btn btn-primary"
              style={{ padding: '0 2.5rem', height: '3rem', fontSize: '1rem', fontWeight: 600 }}
              onClick={() => checkoutMutation.mutate(selectedPlan)}
              disabled={!selectedPlan || checkoutMutation.isLoading || plans.length === 0}
            >
              {checkoutMutation.isLoading ? (
                <>
                  <Loader2 style={{ width: '1rem', height: '1rem', animation: 'spin 1s linear infinite' }} />
                  Redirecting to Stripe…
                </>
              ) : (
                <>
                  Get started
                  <ArrowRight style={{ width: '1rem', height: '1rem' }} />
                </>
              )}
            </button>
            <p className="flex items-center gap-2 text-faint" style={{ fontSize: '0.75rem' }}>
              <Lock style={{ width: '0.75rem', height: '0.75rem' }} />
              Payments secured by Stripe · Cancel anytime
            </p>
          </div>

          {/* Already paid notice */}
          <div className="flex items-center justify-center gap-2 text-muted" style={{ marginTop: '2rem', fontSize: '0.875rem' }}>
            <AlertCircle style={{ width: '1rem', height: '1rem' }} />
            <span>Already paid? <button onClick={() => window.location.reload()} style={{ color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Refresh this page</button></span>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

export default SubscriptionGate
